/**
 * Doctor Strange — „Marvel: Brains and Brawn".
 *
 * DARK PACT: po každom súboji, v ktorom Strange zahral kartu, si smie dať
 * 1 poškodenie — karta potom ide pod balíček a potiahne si novú. Voľba príde
 * po upratovaní, takže karta je vtedy už v odhode.
 *
 * BLIND BOOST: odhoď vrchnú kartu svojho balíčka a pripočítaj jej BOOST.
 */

import { areAdjacent, neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  cardDef,
  dealDamage,
  discardToDeckBottom,
  drawCard,
  emit,
  gainAction,
  getMap,
  getPlayer,
  healFighter,
  millDeck,
  putOnDeck,
  shuffleDeck,
  shuffleHandIntoDeck,
} from '../ops.js';
import { areAllies } from '../players.js';
import { nextInt } from '../rng.js';
import type { CardId, FighterId, PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const STRANGE = 'doctor-strange';
const WONG = 'wong';
const BOLTS = 'doctor_strange.bolts-of-balthakk';
const SUNS = 'doctor_strange.seven-suns-of-cinnibus';
const WINDS = 'doctor_strange.the-winds-of-watoomb';
const DOCTOR = 'doctor_strange.no-really-im-a-doctor';

const figure = (ctx: Ctx, player: PlayerIndex, defId: string) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === defId) ?? null;

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/** „You may BLIND BOOST this card" — voľba `confirm`; `data` = strana:koľký:koľko najviac. */
function askBlindBoost(ctx: Ctx, player: PlayerIndex, source: string, effect: string, data: string, label: string): void {
  if (getPlayer(ctx.state, player).deck.length === 0) return;
  askChoice(ctx, {
    kind: 'option',
    player,
    source,
    label,
    optional: true,
    options: [{ label: 'Blind boost: odhoď vrchnú kartu balíčka', follow: { kind: 'effect', effect, data } }],
  });
}

/** Odhodí vrchnú kartu balíčka a pripočíta jej BOOST k strane súboja. */
export function blindBoost(ctx: Ctx, player: PlayerIndex, s: 'attacker' | 'defender'): number {
  const top = getPlayer(ctx.state, player).deck[0];
  const combat = ctx.state.combat;
  if (!top || !combat) return 0;
  millDeck(ctx, player, 1);
  const amount = cardDef(ctx, top).boost;
  combat.boosts ??= { attacker: 0, defender: 0 };
  combat.boosts[s] += amount;
  return amount;
}

/** „DURING: If Dr. Strange is adjacent to the opposing fighter, ignore the value of your opponent's card." */
const cloakOfLevitation: CardEffect = {
  duringChoice: (e) => {
    const strange = figure(e.ctx, e.player, STRANGE);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    const combat = e.ctx.state.combat;
    if (!combat || !strange || !them || !areAdjacent(getMap(e.ctx), strange.spaceId, them.spaceId)) return;
    (combat.flags ??= []).push(e.role === 'defender' ? 'zeroAttack' : 'zeroDefense');
  },
};

/** „If Wong is adjacent to the opposing fighter, deal 1 damage to the opposing fighter and draw 1 card." */
const steadfastDisciple: CardEffect = {
  after: (e) => {
    const wong = figure(e.ctx, e.player, WONG);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!wong || !them || !areAdjacent(getMap(e.ctx), wong.spaceId, them.spaceId)) return;
    dealDamage(e.ctx, them.id, 1);
    drawCard(e.ctx, e.player);
  },
};

/** „If Dr. Strange and Wong are adjacent, this card's value is 4 instead." */
const masterOfKamarTaj: CardEffect = {
  during: (e, value) => {
    const strange = figure(e.ctx, e.player, STRANGE);
    const wong = figure(e.ctx, e.player, WONG);
    return strange && wong && areAdjacent(getMap(e.ctx), strange.spaceId, wong.spaceId) ? 4 : value;
  },
};

/** „DURING: You may BLIND BOOST this card. AFTER: If you won the combat, gain 1 action." */
const boltsOfBalthakk: CardEffect = {
  duringChoice: (e) => askBlindBoost(e.ctx, e.player, e.cardId, BOLTS, side(e), 'Bolts of Balthakk: smieš blind boostovať'),
  onFollow: (ctx, choice, s) => void blindBoost(ctx, choice.player, s as 'attacker' | 'defender'),
  after: (e) => {
    if (e.won) gainAction(e.ctx, e.player);
  },
};

/**
 * „If the value of this card is above 7, ignore its value. DURING: You may
 * BLIND BOOST this card up to two times." Druhý blind boost sa ponúkne až po
 * prvom; hodnota nad 7 sa vynuluje.
 */
const sevenSuns: CardEffect = {
  duringChoice: (e) =>
    askBlindBoost(e.ctx, e.player, e.cardId, SUNS, `${side(e)}:1`, 'Seven Suns of Cinnibus: smieš blind boostovať (nad 7 = 0)'),
  onFollow: (ctx, choice, data) => {
    const [s, round] = data.split(':') as ['attacker' | 'defender', string];
    blindBoost(ctx, choice.player, s);
    const combat = ctx.state.combat;
    if (!combat) return;
    const total = 3 + (combat.boosts?.[s] ?? 0);
    if (total > 7 && !combat.flags?.includes(`sevenSuns:${s}`)) {
      (combat.flags ??= []).push(`sevenSuns:${s}`, s === 'attacker' ? 'zeroAttack' : 'zeroDefense');
    }
    if (round === '1' && total <= 7) {
      askBlindBoost(ctx, choice.player, choice.source ?? SUNS, SUNS, `${s}:2`, 'Seven Suns of Cinnibus: druhý blind boost? (nad 7 = 0)');
    }
  },
};

/**
 * „IMMEDIATELY: Discard your opponent's card. They reveal cards from their
 * deck until they reveal an attack or versatile card they can play. They play
 * that card and randomly put the rest of the revealed cards on the bottom of
 * their deck." Nová karta dostane IMMEDIATELY v kroku útočníka.
 */
const mistsOfMunnopor: CardEffect = {
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat?.attackCard || combat.bonus || e.role !== 'defender') return;
    const attacker = fighterOrNull(e.ctx, combat.attackerFighter);
    const owner = combat.attackerPlayer;
    const seat = getPlayer(e.ctx.state, owner);
    if (!attacker) return;
    const revealed: CardId[] = [];
    let found: CardId | null = null;
    for (const cardId of seat.deck) {
      revealed.push(cardId);
      const def = cardDef(e.ctx, cardId);
      if ((def.type === 'attack' || def.type === 'versatile') && (!def.character || def.character === attacker.defId)) {
        found = cardId;
        break;
      }
    }
    // Keď v balíčku nie je čím útočiť, pôvodná karta ostáva (inak by súboj ostal bez karty).
    if (!found) return;
    for (const cardId of revealed) emit(e.ctx, { type: 'cardRevealed', player: owner, cardId });
    const old = combat.attackCard;
    seat.discard.push(old);
    emit(e.ctx, { type: 'cardDiscarded', player: owner, cardId: old, reason: 'effect' });
    seat.deck = seat.deck.filter((id) => !revealed.includes(id));
    const rest = revealed.filter((id) => id !== found);
    // „randomly put the rest on the bottom" — náhodné poradie cez seed.
    while (rest.length > 0) seat.deck.push(rest.splice(nextInt(e.ctx.state, rest.length), 1)[0]!);
    combat.attackCard = found;
    emit(e.ctx, { type: 'attackCardReplaced', player: owner, from: old, to: found });
  },
};

/** „If you won the combat, your fighter and each friendly fighter adjacent to them recover health equal to the damage dealt." */
const ringsOfRaggadorr: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    const amount = e.damage ?? 0;
    if (!e.won || !me || amount <= 0) return;
    const around = new Set(neighbors(getMap(e.ctx), me.spaceId));
    const friends = e.ctx.state.fighters.filter(
      (f) => f.id === me.id || (around.has(f.spaceId) && areAllies(e.ctx.state, f.owner, e.player)),
    );
    for (const f of friends) healFighter(e.ctx, f.id, amount);
  },
};

const freeStarts = (ctx: Ctx, except: string[] = []) => {
  const occupied = new Set(ctx.state.fighters.map((f) => f.spaceId));
  return getMap(ctx)
    .spaces.filter((s) => s.start !== undefined && !occupied.has(s.id) && !except.includes(s.id))
    .map((s) => s.id);
};

/**
 * „Your opponent places Dr. Strange in a starting space. Place the opposing
 * fighter in another starting space. All players shuffle their decks."
 */
const windsOfWatoomb: CardEffect = {
  after: (e) => {
    const strange = figure(e.ctx, e.player, STRANGE);
    const opponent = opponentOf(e);
    if (strange && opponent !== null) {
      askChoice(e.ctx, {
        kind: 'place',
        player: opponent,
        source: e.cardId,
        label: 'The Winds of Watoomb: polož Doctora Strangea na štartový priestor',
        optional: false,
        fighterId: strange.id,
        spaces: freeStarts(e.ctx),
      });
    }
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'The Winds of Watoomb: súper na iný štart',
      optional: false,
      follow: { kind: 'effect', effect: WINDS, data: e.opponentFighterId ?? '' },
    });
  },
  onFollow: (ctx, choice, targetId) => {
    const target = fighterOrNull(ctx, targetId || null);
    const spaces = freeStarts(ctx);
    if (target && spaces.length > 0) {
      askChoice(ctx, {
        kind: 'place',
        player: choice.player,
        source: choice.source,
        label: 'The Winds of Watoomb: polož súpera na iný štartový priestor',
        optional: false,
        fighterId: target.id,
        spaces,
      });
    }
    ctx.state.players.forEach((seat, player) => {
      if (!seat.eliminated) shuffleDeck(ctx, player);
    });
  },
};

/** „Shuffle your hand into your deck. Draw 5 cards. Gain 1 action." Schéma sama sa nemieša. */
const eyeOfAgamotto: CardEffect = {
  scheme: (e) => {
    shuffleHandIntoDeck(e.ctx, e.player, [e.cardId]);
    for (let i = 0; i < 5; i++) drawCard(e.ctx, e.player);
    gainAction(e.ctx, e.player);
  },
};

/**
 * „Reveal a card from your hand and put it on top of your deck. Dr. Strange
 * or a friendly fighter adjacent to him recovers health equal to that card's
 * boost value."
 */
const noReallyImADoctor: CardEffect = {
  scheme: (e) => {
    if (getPlayer(e.ctx.state, e.player).hand.length <= 1) return;
    askChoice(e.ctx, {
      kind: 'card',
      player: e.player,
      source: e.cardId,
      label: "No, Really, I'm a Doctor: ukáž kartu z ruky — pôjde navrch balíčka",
      optional: false,
      from: 'hand',
      purpose: 'effect',
      except: [e.cardId],
    });
  },
  onCardChosen: (ctx, choice, cardId) => {
    const amount = cardDef(ctx, cardId).boost;
    emit(ctx, { type: 'cardRevealed', player: choice.player, cardId });
    putOnDeck(ctx, choice.player, cardId);
    const strange = figure(ctx, choice.player, STRANGE);
    if (!strange || amount <= 0) return;
    const around = new Set(neighbors(getMap(ctx), strange.spaceId));
    const patients: FighterId[] = ctx.state.fighters
      .filter((f) => f.id === strange.id || (around.has(f.spaceId) && areAllies(ctx.state, f.owner, choice.player)))
      .map((f) => f.id);
    askChoice(ctx, {
      kind: 'option',
      player: choice.player,
      source: choice.source,
      label: `No, Really, I'm a Doctor: kto si vylieči ${amount}?`,
      optional: false,
      options: patients.map((id) => ({
        label: fighterOrNull(ctx, id)?.name ?? id,
        follow: { kind: 'effect' as const, effect: DOCTOR, data: `${id}:${amount}` },
      })),
    });
  },
  onFollow: (ctx, _choice, data) => {
    const [id, amount] = data.split(':') as [string, string];
    if (fighterOrNull(ctx, id)) healFighter(ctx, id, Number(amount));
  },
};

export const doctorStrangeCards: EffectRegistry = {
  'doctor_strange.cloak-of-levitation': cloakOfLevitation,
  'doctor_strange.steadfast-disciple': steadfastDisciple,
  'doctor_strange.master-of-kamar-taj': masterOfKamarTaj,
  [BOLTS]: boltsOfBalthakk,
  [SUNS]: sevenSuns,
  'doctor_strange.the-mists-of-munnopor': mistsOfMunnopor,
  'doctor_strange.the-rings-of-raggadorr': ringsOfRaggadorr,
  [WINDS]: windsOfWatoomb,
  'doctor_strange.eye-of-agamotto': eyeOfAgamotto,
  [DOCTOR]: noReallyImADoctor,
  'doctor_strange.feint': feint,
};

export const doctorStrangeSpecial: HeroSpecial = {
  name: 'Dark Pact',
  /**
   * „After each combat, if Doctor Strange played a card, you may deal 1 damage
   * to him. If you do, put that card on the bottom of your deck and draw 1 card."
   */
  afterCombat: (ctx, player) => {
    const combat = ctx.state.combat;
    const strange = figure(ctx, player, STRANGE);
    if (!combat || !strange) return;
    const card =
      combat.attackerFighter === strange.id && !combat.bonus
        ? combat.attackCard
        : combat.defenderFighter === strange.id
          ? combat.defenseCard
          : null;
    if (!card) return;
    askChoice(ctx, {
      kind: 'option',
      player,
      source: null,
      label: `Dark Pact: 1 poškodenie Strangeovi — ${cardDef(ctx, card).name} pod balíček a nová karta?`,
      optional: true,
      options: [{ label: 'Áno, 1 poškodenie', follow: { kind: 'special', hero: STRANGE, data: card } }],
    });
  },
  onFollow: (ctx, player, cardId) => {
    const strange = figure(ctx, player, STRANGE);
    if (!strange) return;
    dealDamage(ctx, strange.id, 1);
    if (getPlayer(ctx.state, player).discard.includes(cardId)) discardToDeckBottom(ctx, player, cardId);
    drawCard(ctx, player);
  },
};
